import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'components/ui/button';
import { meetings, type MobileMeetingWaiter } from 'services/api/meetingsClient';

/** Native SDKs have no lobby API: the server holds native join tokens until this host admits them. */
export function MobileWaitingRoomHostPanel({ meetingId }: { meetingId: string }) {
  const [participants, setParticipants] = useState<MobileMeetingWaiter[]>([]);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<string>();
  const mounted = useRef(true);
  const refreshVersion = useRef(0);
  const refresh = useCallback(async () => {
    const version = ++refreshVersion.current;
    try {
      const result = await meetings.getMobileWaiters(meetingId);
      if (mounted.current && version === refreshVersion.current) { setParticipants(result.participants); setError(''); }
    } catch {
      if (mounted.current && version === refreshVersion.current) setError('Unable to refresh the waiting room. Retrying…');
    }
  }, [meetingId]);
  useEffect(() => {
    mounted.current = true;
    void refresh();
    const timer = setInterval(() => void refresh(), 5000);
    return () => { mounted.current = false; refreshVersion.current += 1; clearInterval(timer); };
  }, [refresh]);
  const decide = async (id: string, action: 'admit' | 'deny') => {
    if (pending) return;
    setPending(id); setError('');
    try { await meetings.decideMobileAccess(meetingId, id, action); if (mounted.current) await refresh(); }
    catch { if (mounted.current) setError('Unable to update admission. Please try again.'); }
    finally { if (mounted.current) setPending(undefined); }
  };
  if (!participants.length && !error) return null;
  return <section aria-label="Mobile waiting room" className="space-y-3">
    <h3 className="text-sm font-semibold">Waiting to join</h3>
    {error && <p role="alert" className="text-sm text-red-200">{error}</p>}
    <ul className="space-y-2">{participants.map(person => <li key={person.id} className="flex items-center gap-2 rounded-lg bg-white/5 p-3">
      <span className="min-w-0 flex-1 truncate">{person.display_name || 'Guest'}</span>
      <Button size="auto" className="px-3 py-2 text-xs" disabled={Boolean(pending)} onClick={() => void decide(person.id, 'admit')}>Admit</Button>
      <Button size="auto" className="px-3 py-2 text-xs" variant="secondary" disabled={Boolean(pending)} onClick={() => void decide(person.id, 'deny')}>Deny</Button>
    </li>)}</ul>
  </section>;
}
